import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CMS_POST_API_CONFIG, BlogPostConfig } from '../config';
import {
  BlogPost,
  BlogPostPopulated,
  CreateBlogPostDto,
  UpdateBlogPostDto,
  PublishBlogPostDto,
  AssignGroupsDto,
  RemoveGroupsDto,
  BlogPostQueryParams,
  PublicBlogPostQueryParams,
  ApiResponse,
  PaginatedResponse,
  DashboardStats,
  ExportFormat,
  BlogPostSelectorItem,
  PublishStatus,
  ReaderFeedParams,
  ReaderFeedResponse,
  ReaderSummaryResponse,
  ReaderSummaryPost
} from '../models';

/**
 * Service for managing blog posts via the CMS API
 * 
 * This service provides comprehensive methods for:
 * - CRUD operations on blog posts
 * - Public and authenticated endpoints
 * - Dashboard statistics
 * - Group management
 * - Content export functionality
 * 
 * @remarks
 * Authentication is handled by HTTP interceptors provided by the consuming application.
 * Rate limiting is documented per endpoint but should be handled by the consuming app.
 * 
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private blogPostService: BlogPostService) {}
 * 
 * // Create a blog post
 * this.blogPostService.createBlogPost({
 *   title: 'My Post',
 *   content: 'Content here',
 *   orgId: 'org123',
 *   postTypeId: 'type123'
 * }).subscribe(response => {
 *   console.log('Created:', response.data);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class BlogPostService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CMS_POST_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/blog-posts`;
    this.logDebug('BlogPostService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // PUBLIC ENDPOINTS (No Authentication Required)
  // ============================================================================

  /**
   * Get all public blog posts with optional filtering and pagination
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of paginated public blog posts
   * 
   * @remarks
   * Rate limit: 100 requests per 15 minutes
   * Supports full-text search with relevance scoring
   * Date filtering available via dateFrom, dateTo, updatedFrom, updatedTo
   * 
   * @example
   * ```typescript
   * this.blogPostService.getPublicBlogPosts({
   *   tags: ['technology', 'tutorial'],
   *   dateFrom: '2024-01-01',
   *   dateTo: '2024-12-31',
   *   page: 1,
   *   limit: 10,
   *   sortBy: SortBy.CREATED_AT,
   *   sortOrder: SortOrder.DESC
   * }).subscribe(response => {
   *   console.log('Posts:', response.data);
   *   console.log('Total:', response.pagination.total);
   * });
   * ```
   */
  getPublicBlogPosts(params?: PublicBlogPostQueryParams): Observable<PaginatedResponse<BlogPostPopulated>> {
    const httpParams = this.buildHttpParams(params);
    return this.http.get<PaginatedResponse<BlogPostPopulated>>(`${this.apiUrl}/public`, { params: httpParams })
      .pipe(
        map(response => ({ ...response, data: response.data.map(post => this._normalizePublicPost(post)) })),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get a single public blog post by ID or slug
   * 
   * @param identifier - Blog post ID (MongoDB ObjectId) or slug
   * @returns Observable of the blog post
   * 
   * @remarks
   * Rate limit: 200 requests per 15 minutes
   * 
   * @example
   * ```typescript
   * // By ID
   * this.blogPostService.getPublicBlogPost('507f1f77bcf86cd799439011')
   *   .subscribe(response => console.log(response.data));
   * 
   * // By slug
   * this.blogPostService.getPublicBlogPost('my-awesome-post')
   *   .subscribe(response => console.log(response.data));
   * ```
   */
  getPublicBlogPost(identifier: string): Observable<ApiResponse<BlogPostPopulated>> {
    return this.http.get<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/public/${identifier}`)
      .pipe(
        map(response => response.data
          ? { ...response, data: this._normalizePublicPost(response.data) }
          : response),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * The `/public` blog-post endpoints currently serialize the populated
   * author/post-type refs under the legacy field names `author`/`postType`
   * instead of `authorId`/`postTypeId`, which every other endpoint and
   * consumer (post-detail, comments, bookmarks, SEO, the editor UI, etc.)
   * expects on `BlogPostPopulated`. Normalize here, at the one boundary
   * where this raw response enters the app, rather than special-casing it
   * in every downstream component.
   */
  private _normalizePublicPost(raw: BlogPostPopulated): BlogPostPopulated {
    const legacy = raw as unknown as { author?: unknown; postType?: unknown };
    return {
      ...raw,
      authorId: raw.authorId ?? (legacy.author as BlogPostPopulated['authorId']) ?? null,
      postTypeId: raw.postTypeId ?? (legacy.postType as BlogPostPopulated['postTypeId']),
    };
  }

  /**
   * Convenience method to get public blog post data without wrapper
   * 
   * @param identifier - Blog post ID or slug
   * @returns Observable of the blog post data
   */
  getPublicBlogPostData(identifier: string): Observable<BlogPostPopulated | undefined> {
    return this.getPublicBlogPost(identifier).pipe(
      map(response => response.data)
    );
  }

  // ============================================================================
  // PROTECTED ENDPOINTS (Authentication Required)
  // ============================================================================

  /**
   * Create a new blog post
   * 
   * @param blogPostData - Blog post data to create
   * @returns Observable of the created blog post with populated fields
   * 
   * @remarks
   * User must be a member of the organization
   * Slug is auto-generated from title
   * Excerpt and meta description are auto-generated if not provided
   * Supports SEO meta fields: ogUrl, ogImage, twitterCard, twitterImage, keywords, coverImageAlt
   * 
   * @example
   * ```typescript
   * this.blogPostService.createBlogPost({
   *   title: 'Getting Started with Angular',
   *   content: 'Angular is a powerful framework...',
   *   contentFormat: ContentFormat.MARKDOWN,
   *   ogUrl: 'https://example.com/angular-guide',
   *   ogImage: 'https://example.com/images/angular.jpg',
   *   orgId: 'org123',
   *   postTypeId: 'type123',
   *   tags: ['angular', 'tutorial'],
   *   publishStatus: PublishStatus.DRAFT
   * }).subscribe(response => {
   *   console.log('Created:', response.data);
   * });
   * ```
   */
  createBlogPost(blogPostData: CreateBlogPostDto): Observable<ApiResponse<BlogPostPopulated>> {
    this.logDebug('Creating blog post', blogPostData);
    return this.http.post<ApiResponse<BlogPostPopulated>>(this.apiUrl, blogPostData)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get all blog posts with filtering and pagination
   * 
   * @param params - Query parameters for filtering
   * @returns Observable of paginated blog posts
   * 
   * @remarks
   * Supports full-text search with relevance scoring
   * Date filtering available via dateFrom, dateTo, updatedFrom, updatedTo (ISO 8601 format)
   * 
   * @example
   * ```typescript
   * this.blogPostService.getBlogPosts({
   *   orgId: 'org123',
   *   publishStatus: PublishStatus.PUBLIC,
   *   tags: ['angular'],
   *   updatedFrom: '2024-01-01T00:00:00Z',
   *   page: 1,
   *   limit: 20
   * }).subscribe(response => {
   *   console.log('Posts:', response.data);
   * });
   * ```
   */
  getBlogPosts(params?: BlogPostQueryParams): Observable<PaginatedResponse<BlogPostPopulated>> {
    const httpParams = this.buildHttpParams(params);
    return this.http.get<PaginatedResponse<BlogPostPopulated>>(this.apiUrl, { params: httpParams })
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get a single blog post by ID or slug
   * 
   * @param identifier - Blog post ID or slug
   * @returns Observable of the blog post
   * 
   * @example
   * ```typescript
   * this.blogPostService.getBlogPost('507f1f77bcf86cd799439011')
   *   .subscribe(response => console.log(response.data));
   * ```
   */
  getBlogPost(identifier: string): Observable<ApiResponse<BlogPostPopulated>> {
    return this.http.get<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/${identifier}`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get a blog post by slug
   * 
   * @param slug - Blog post slug
   * @returns Observable of the blog post
   * 
   * @example
   * ```typescript
   * this.blogPostService.getBlogPostBySlug('my-awesome-post')
   *   .subscribe(response => console.log(response.data));
   * ```
   */
  getBlogPostBySlug(slug: string): Observable<ApiResponse<BlogPostPopulated>> {
    return this.http.get<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/slug/${slug}`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update an existing blog post
   * 
   * @param id - Blog post ID
   * @param updateData - Fields to update
   * @returns Observable of the updated blog post
   * 
   * @remarks
   * Only the author can update their posts
   * Slug is regenerated if title changes
   * Supports updating SEO meta fields: ogUrl, ogImage, twitterCard, twitterImage, keywords, coverImageAlt
   * Pass null explicitly to clear a field: { keywords: null, coverImageAlt: null }
   * 
   * @example
   * ```typescript
   * this.blogPostService.updateBlogPost('507f1f77bcf86cd799439011', {
   *   title: 'Updated Title',
   *   ogImage: 'https://example.com/images/updated.jpg',
   *   tags: ['angular', 'typescript', 'web-development']
   * }).subscribe(response => {
   *   console.log('Updated:', response.data);
   * });
   * ```
   */
  updateBlogPost(id: string, updateData: UpdateBlogPostDto): Observable<ApiResponse<BlogPostPopulated>> {
    this.logDebug('Updating blog post', { id, updateData });
    return this.http.put<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/${id}`, updateData)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete a blog post
   * 
   * @param id - Blog post ID
   * @returns Observable of the deletion response
   * 
   * @remarks
   * Only the author can delete their posts
   * 
   * @example
   * ```typescript
   * this.blogPostService.deleteBlogPost('507f1f77bcf86cd799439011')
   *   .subscribe(response => {
   *     console.log(response.message);
   *   });
   * ```
   */
  deleteBlogPost(id: string): Observable<ApiResponse<void>> {
    this.logDebug('Deleting blog post', { id });
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // ============================================================================
  // PUBLISH/UNPUBLISH OPERATIONS
  // ============================================================================

  /**
   * Publish a blog post
   * 
   * @param id - Blog post ID
   * @param publishData - Optional publish status (defaults to public)
   * @returns Observable of the published blog post
   * 
   * @remarks
   * Only the author can publish their posts
   * Sets published: true
   * 
   * @example
   * ```typescript
   * // Publish as public (default)
   * this.blogPostService.publishBlogPost('507f1f77bcf86cd799439011')
   *   .subscribe(response => console.log('Published:', response.data));
   * 
   * // Publish as internal only
   * this.blogPostService.publishBlogPost('507f1f77bcf86cd799439011', {
   *   publishStatus: PublishStatus.INTERNAL
   * }).subscribe(response => console.log('Published internally:', response.data));
   * ```
   */
  publishBlogPost(id: string, publishData?: PublishBlogPostDto): Observable<ApiResponse<BlogPostPopulated>> {
    this.logDebug('Publishing blog post', { id, publishData });
    return this.http.patch<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/${id}/publish`, publishData || {})
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Unpublish a blog post
   * 
   * @param id - Blog post ID
   * @returns Observable of the unpublished blog post
   * 
   * @remarks
   * Only the author can unpublish their posts
   * Sets published: false and publishStatus: 'draft'
   * 
   * @example
   * ```typescript
   * this.blogPostService.unpublishBlogPost('507f1f77bcf86cd799439011')
   *   .subscribe(response => console.log('Unpublished:', response.data));
   * ```
   */
  unpublishBlogPost(id: string): Observable<ApiResponse<BlogPostPopulated>> {
    this.logDebug('Unpublishing blog post', { id });
    return this.http.patch<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/${id}/unpublish`, {})
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // ============================================================================
  // GROUP MANAGEMENT
  // ============================================================================

  /**
   * Assign groups to a blog post
   * 
   * @param id - Blog post ID
   * @param groupData - Group names to assign
   * @returns Observable of the updated blog post
   * 
   * @remarks
   * Only the author can assign groups
   * Duplicates are handled automatically
   * 
   * @example
   * ```typescript
   * this.blogPostService.assignGroups('507f1f77bcf86cd799439011', {
   *   groupNames: ['Engineering', 'Product', 'Leadership']
   * }).subscribe(response => {
   *   console.log('Groups assigned:', response.data?.groups);
   * });
   * ```
   */
  assignGroups(id: string, groupData: AssignGroupsDto): Observable<ApiResponse<BlogPostPopulated>> {
    this.logDebug('Assigning groups to blog post', { id, groupData });
    return this.http.post<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/${id}/groups`, groupData)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Remove groups from a blog post
   * 
   * @param id - Blog post ID
   * @param groupData - Group names to remove
   * @returns Observable of the updated blog post
   * 
   * @remarks
   * Only the author can remove groups
   * 
   * @example
   * ```typescript
   * this.blogPostService.removeGroups('507f1f77bcf86cd799439011', {
   *   groupNames: ['Engineering']
   * }).subscribe(response => {
   *   console.log('Groups removed:', response.data?.groups);
   * });
   * ```
   */
  removeGroups(id: string, groupData: RemoveGroupsDto): Observable<ApiResponse<BlogPostPopulated>> {
    this.logDebug('Removing groups from blog post', { id, groupData });
    return this.http.delete<ApiResponse<BlogPostPopulated>>(`${this.apiUrl}/${id}/groups`, {
      body: groupData
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get blog posts by group name
   * 
   * @param groupName - Group name to filter by
   * @returns Observable of blog posts in the group
   * 
   * @example
   * ```typescript
   * this.blogPostService.getBlogPostsByGroup('Engineering')
   *   .subscribe(response => {
   *     console.log(`Found ${response.count} posts in Engineering group`);
   *   });
   * ```
   */
  getBlogPostsByGroup(groupName: string): Observable<ApiResponse<BlogPostPopulated[]>> {
    return this.http.get<ApiResponse<BlogPostPopulated[]>>(`${this.apiUrl}/group/${encodeURIComponent(groupName)}`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // ============================================================================
  // DASHBOARD & STATISTICS
  // ============================================================================

  /**
   * Get dashboard statistics for an organization
   * 
   * @param orgId - Organization ID
   * @returns Observable of dashboard statistics
   * 
   * @remarks
   * User must be a member or creator of the organization
   * Includes overview, breakdowns, trends, and recent activity
   * 
   * @example
   * ```typescript
   * this.blogPostService.getDashboardStats('org123')
   *   .subscribe(response => {
   *     const stats = response.data;
   *     console.log('Total posts:', stats?.overview.totalPosts);
   *     console.log('Published:', stats?.overview.publishedPosts);
   *     console.log('Top tags:', stats?.topTags);
   *   });
   * ```
   */
  getDashboardStats(orgId: string): Observable<ApiResponse<DashboardStats>> {
    return this.http.get<ApiResponse<DashboardStats>>(`${this.apiUrl}/dashboard/stats/${orgId}`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  // ============================================================================
  // READER ENDPOINTS (Authenticated — Personalized Access-Controlled Content)
  // ============================================================================

  /**
   * Get the personalized, paginated reader feed for the authenticated user.
   *
   * Returns blog posts visible to the user based on their organisation and
   * group memberships. Both `internal` and `public` posts are returned;
   * `draft` posts are never included.
   *
   * @param params - Optional query parameters for filtering and pagination
   * @returns Observable of the reader feed response
   *
   * @remarks
   * **`meta` is only present on page 1** (or when `page` is omitted).
   * Cache it locally (e.g. in a `BehaviorSubject` or component state) after
   * the first fetch. Do not re-request it on subsequent paginated calls.
   * Use the cached `meta` to populate filter dropdowns for organisations,
   * groups, and post types.
   *
   * **Search debouncing:** When passing a `search` param, debounce the input
   * by 400–600 ms before calling this method. The search endpoint has a
   * separate rate limit of 50 requests / 15 minutes per IP.
   *
   * **Empty state:** If the user belongs to no organisations the API returns
   * `{ success: true, count: 0, data: [] }` — not a 4xx error. Treat this as
   * an empty-state UI scenario rather than routing to an error boundary.
   *
   * @example
   * ```typescript
   * // First page — capture meta for filter dropdowns
   * this.blogPostService.getReaderFeed({ page: 1, limit: 20 })
   *   .subscribe(response => {
   *     this.posts = response.data;
   *     if (response.meta) {
   *       this.filterMeta = response.meta; // cache — won't appear on page 2+
   *     }
   *   });
   *
   * // Subsequent pages — meta absent, use cached value
   * this.blogPostService.getReaderFeed({ page: 2, limit: 20 })
   *   .subscribe(response => {
   *     this.posts = [...this.posts, ...response.data];
   *   });
   *
   * // With filters
   * this.blogPostService.getReaderFeed({
   *   orgId: 'org123',
   *   postTypeId: 'type456',
   *   search: 'typescript',
   *   sortBy: SortBy.UPDATED_AT,
   *   sortOrder: SortOrder.DESC
   * }).subscribe(response => console.log(response.data));
   * ```
   */
  getReaderFeed(params?: ReaderFeedParams): Observable<ReaderFeedResponse> {
    this.logDebug('Fetching reader feed', params);
    const httpParams = this.buildHttpParams(params);
    return this.http.get<ReaderFeedResponse>(`${this.apiUrl}/reader/feed`, { params: httpParams })
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get a lightweight, unpaginated summary of all posts visible to the
   * authenticated user — intended for sidebar and navigation trees.
   *
   * Returns the same access-controlled set as the reader feed but with
   * minimal fields only: `_id`, `slug`, `title`, `postTypeId`, `organization`.
   *
   * @returns Observable of the reader summary response
   *
   * @remarks
   * Results are **pre-sorted server-side** by organisation name → post type
   * name → title (all ascending alphabetically). No client-side sorting is
   * required.
   *
   * **Field name:** Each item exposes `organization` (not `orgId`) for the
   * organisation ref. Ensure template bindings and interface consumers use
   * `item.organization`, not `item.orgId`.
   *
   * No query parameters are accepted by this endpoint.
   *
   * @example
   * ```typescript
   * this.blogPostService.getReaderSummary()
   *   .subscribe(response => {
   *     this.navItems = response.data;
   *   });
   * ```
   */
  getReaderSummary(): Observable<ReaderSummaryResponse> {
    this.logDebug('Fetching reader summary');
    return this.http.get<ReaderSummaryResponse>(`${this.apiUrl}/reader/summary`)
      .pipe(
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Convenience method — get reader summary posts without the response wrapper.
   *
   * @returns Observable of `ReaderSummaryPost[]`
   *
   * @example
   * ```typescript
   * this.blogPostService.getReaderSummaryData()
   *   .subscribe(posts => {
   *     this.navItems = posts; // ReaderSummaryPost[]
   *   });
   * ```
   */
  getReaderSummaryData(): Observable<ReaderSummaryPost[]> {
    return this.getReaderSummary().pipe(
      map(response => response.data)
    );
  }

  // ============================================================================
  // EXPORT FUNCTIONALITY
  // ============================================================================

  /**
   * Export blog post content in specified format
   * 
   * @param id - Blog post ID
   * @param format - Export format (html, markdown, or json)
   * @returns Observable of Blob for file download
   * 
   * @remarks
   * User must have access to the organization
   * Converts content format if needed
   * Filename: {slug}.{extension}
   * 
   * @example
   * ```typescript
   * this.blogPostService.exportBlogPost('507f1f77bcf86cd799439011', ExportFormat.MARKDOWN)
   *   .subscribe(blob => {
   *     const url = window.URL.createObjectURL(blob);
   *     const a = document.createElement('a');
   *     a.href = url;
   *     a.download = 'my-post.md';
   *     a.click();
   *   });
   * ```
   */
  exportBlogPost(id: string, format: ExportFormat): Observable<Blob> {
    this.logDebug('Exporting blog post', { id, format });
    return this.http.get(`${this.apiUrl}/${id}/export/${format}`, {
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Export and download blog post
   * 
   * @param id - Blog post ID
   * @param format - Export format
   * @param filename - Custom filename (optional)
   * 
   * @example
   * ```typescript
   * this.blogPostService.exportAndDownload(
   *   '507f1f77bcf86cd799439011',
   *   ExportFormat.HTML,
   *   'my-custom-filename.html'
   * );
   * ```
   */
  exportAndDownload(id: string, format: ExportFormat, filename?: string): void {
    this.exportBlogPost(id, format).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `blog-post.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Export failed:', error);
      }
    });
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get blog post data without the API response wrapper
   * 
   * @param identifier - Blog post ID or slug
   * @returns Observable of blog post data or undefined
   */
  getBlogPostData(identifier: string): Observable<BlogPostPopulated | undefined> {
    return this.getBlogPost(identifier).pipe(
      map(response => response.data)
    );
  }

  /**
   * Get blog posts data without the API response wrapper
   * 
   * @param params - Query parameters
   * @returns Observable of blog posts array
   */
  getBlogPostsData(params?: BlogPostQueryParams): Observable<BlogPostPopulated[]> {
    return this.getBlogPosts(params).pipe(
      map(response => response.data)
    );
  }

  /**
   * Get dashboard statistics data without the API response wrapper
   * 
   * @param orgId - Organization ID
   * @returns Observable of dashboard stats or undefined
   */
  getDashboardStatsData(orgId: string): Observable<DashboardStats | undefined> {
    return this.getDashboardStats(orgId).pipe(
      map(response => response.data)
    );
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Build HTTP params from query parameters object
   * 
   * @param params - Query parameters
   * @returns HttpParams instance
   */
  private buildHttpParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    Object.keys(params).forEach(key => {
      const value = params[key];
      
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // Handle array parameters
          value.forEach(item => {
            httpParams = httpParams.append(key, String(item));
          });
        } else {
          httpParams = httpParams.set(key, String(value));
        }
      }
    });

    return httpParams;
  }

  /**
   * Handle HTTP errors
   * 
   * @param error - HTTP error response
   * @returns Observable that throws formatted error
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Backend returned an unsuccessful response code
      const apiError = error.error;

      if (error.status === 413) {
        errorMessage = 'Post content exceeds the 2 MB size limit. Please reduce the content size and try again.';
      } else if (apiError && typeof apiError === 'object' && 'message' in apiError) {
        errorMessage = apiError.message;
      } else {
        errorMessage = `Error ${error.status}: ${error.statusText}`;
      }
    }

    this.logDebug('HTTP Error', {
      status: error.status,
      message: errorMessage,
      error: error.error
    });

    return throwError(() => ({
      success: false,
      message: errorMessage,
      statusCode: error.status,
      originalError: error
    }));
  }

  /**
   * Search blog posts for selector component (lightweight)
   * Returns minimal data needed for selection with optional enhanced fields
   * 
   * @param params - Search parameters including orgId, search term, and all supported filters
   * @returns Observable of blog post selector items
   * 
   * @remarks
   * This is an optimized endpoint for the BlogPostSelectorComponent.
   * 
   * **Supported Filters:**
   * - Text search (title, content, excerpt)
   * - Publish status (draft, internal, public)
   * - Date ranges (creation and update dates)
   * - Author ID
   * - Post type ID
   * 
   * **Response:**
   * Always includes: _id, title, publishStatus
   * Optionally includes (when filters active): author, postType, createdAt, updatedAt
   * 
   * **API Rate Limits:**
   * - Protected endpoint: Standard rate limits apply
   * - Max limit per request: 50 (API constraint)
   * 
   * @example
   * ```typescript
   * // Basic search
   * this.blogPostService.searchForSelector({
   *   orgId: 'org123',
   *   search: 'angular',
   *   limit: 50
   * }).subscribe(items => console.log('Found:', items));
   * 
   * // With date range and author filter
   * this.blogPostService.searchForSelector({
   *   orgId: 'org123',
   *   dateFrom: '2024-01-01',
   *   dateTo: '2024-12-31',
   *   authorId: 'author123',
   *   publishStatus: PublishStatus.PUBLIC
   * }).subscribe(items => console.log('Filtered:', items));
   * ```
   */
  searchForSelector(params: {
    orgId: string;
    search?: string;
    publishStatus?: PublishStatus | 'all';
    dateFrom?: string;
    dateTo?: string;
    updateFrom?: string;
    updateTo?: string;
    authorId?: string;
    postTypeId?: string;
    excludeIds?: string[];
    limit?: number;
    page?: number;
  }): Observable<BlogPostSelectorItem[]> {
    // Build HTTP params dynamically - only include defined values
    let httpParams = new HttpParams()
      .set('orgId', params.orgId)
      .set('limit', params.limit?.toString() || '50');
    
    // Add optional search term
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    
    // Add optional publish status filter (skip if 'all')
    if (params.publishStatus && params.publishStatus !== 'all') {
      httpParams = httpParams.set('publishStatus', params.publishStatus);
    }
    
    // Add optional date range filters (creation date)
    if (params.dateFrom) {
      httpParams = httpParams.set('dateFrom', params.dateFrom);
    }
    if (params.dateTo) {
      httpParams = httpParams.set('dateTo', params.dateTo);
    }
    
    // Add optional date range filters (update date)
    if (params.updateFrom) {
      httpParams = httpParams.set('updatedFrom', params.updateFrom);
    }
    if (params.updateTo) {
      httpParams = httpParams.set('updatedTo', params.updateTo);
    }
    
    // Add optional author filter
    if (params.authorId) {
      httpParams = httpParams.set('authorId', params.authorId);
    }
    
    // Add optional post type filter
    if (params.postTypeId) {
      httpParams = httpParams.set('postTypeId', params.postTypeId);
    }
    
    // Add page number
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    
    // Note: excludeIds filtering is handled client-side
    // Server-side implementation can be added later if needed
    
    // Determine if we need enhanced fields based on active filters
    const needsEnhancedFields = !!(
      params.authorId || 
      params.postTypeId || 
      params.dateFrom || 
      params.dateTo || 
      params.updateFrom || 
      params.updateTo
    );
    
    return this.http.get<PaginatedResponse<BlogPostPopulated>>(this.apiUrl, { params: httpParams })
      .pipe(
        map(response => {
          let items: BlogPostSelectorItem[] = response.data.map(post => {
            const baseItem: BlogPostSelectorItem = {
              _id: post._id,
              title: post.title,
              publishStatus: post.publishStatus
            };
            
            // Include enhanced fields if filters are active
            if (needsEnhancedFields) {
              // Add author information if available (using 'authorId' field from BlogPostPopulated)
              if (post.authorId && typeof post.authorId === 'object') {
                baseItem.author = {
                  _id: post.authorId._id,
                  name: post.authorId.name,
                  email: post.authorId.email
                };
              }
              
              // Add post type information if available (using 'postType' field from BlogPostPopulated)
              if (post.postTypeId && typeof post.postTypeId === 'object') {
                baseItem.postType = {
                  _id: post.postTypeId._id,
                  typeName: post.postTypeId.typeName
                };
              }
              
              // Add date information (dates are already in string format from API)
              if (post.createdAt) {
                baseItem.createdAt = post.createdAt;
              }
              if (post.updatedAt) {
                baseItem.updatedAt = post.updatedAt;
              }
            }
            
            return baseItem;
          });
          
          // Filter out excluded IDs if provided (client-side filtering)
          if (params.excludeIds && params.excludeIds.length > 0) {
            items = items.filter(item => !params.excludeIds!.includes(item._id));
          }
          
          return items;
        }),
        catchError(error => {
          console.error('Search for selector failed:', error);
          this.logDebug('searchForSelector error', { params, error: error.message });
          return of([]);
        })
      );
  }

  /**
   * Log debug messages if debug mode is enabled
   * 
   * @param message - Debug message
   * @param data - Optional data to log
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[BlogPostService] ${message}`, data || '');
    }
  }
}
